import { useState } from "react";
import useReveal from "../hooks/useReveal";
import { Mail, Phone, MapPin, Instagram, Facebook, Check, Loader2 } from "lucide-react";
import { brand, philosophyImage } from "../data/mock";
import { useToast } from "../hooks/use-toast";
import { getSessionId, trackEvent } from "../lib/analytics";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

export default function Contact() {
  useReveal();
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: "", email: "", phone: "", eventType: "", date: "", guests: "", budget: "", message: "", website: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [formStartFired, setFormStartFired] = useState(false);

  const updateField = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    if (!formStartFired) {
      setFormStartFired(true);
      trackEvent("form_start", { path: "/contact" });
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);

    // Local backup so nothing is lost even if the network fails
    try {
      const store = JSON.parse(localStorage.getItem("sws_enquiries") || "[]");
      store.push({ ...form, at: new Date().toISOString() });
      localStorage.setItem("sws_enquiries", JSON.stringify(store));
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        console.error("Failed to persist enquiry to localStorage", err);
      }
    }

    try {
      const payload = {
        session_id: getSessionId() || undefined,
        name: form.name,
        email: form.email,
        phone: form.phone,
        eventType: form.eventType,
        date: form.date,
        guests: form.guests,
        budget: form.budget,
        message: form.message,
        website: form.website, // honeypot
      };
      const res = await axios.post(`${API}/track/enquiry`, payload, {
        timeout: 15000,
        validateStatus: () => true,
      });
      if (res.status >= 400) {
        const detail = res.data?.detail || "Submission failed";
        throw new Error(detail);
      }

      setSubmitted(true);
      toast({
        title: "Enquiry received",
        description: "Thank you — Shubhra will be in touch within 48 hours.",
      });
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        console.error("Enquiry submission failed", err);
      }
      toast({
        title: "Couldn't send enquiry",
        description: `Please try again or email us directly at ${brand.email}.`,
        variant: "destructive",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="pt-32">
      <section className="mx-auto max-w-[1400px] px-6 md:px-10 py-16">
        <div className="kicker reveal">Contact</div>
        <h1 className="font-display text-4xl md:text-7xl leading-[1.02] mt-6 max-w-4xl reveal">
          Let&rsquo;s begin your <span className="italic">celebration story</span>.
        </h1>
        <p className="mt-8 max-w-xl text-[hsl(var(--ink-soft))] leading-relaxed reveal">
          Share a few details about your event and we&rsquo;ll be in touch within 48 hours to arrange a
          complimentary consultation.
        </p>
      </section>

      <section className="mx-auto max-w-[1400px] px-6 md:px-10 pb-24 grid md:grid-cols-12 gap-10 md:gap-16">
        <div className="md:col-span-7 reveal">
          {submitted ? (
            <div className="border border-[hsl(var(--line))] p-12 text-center">
              <Check className="mx-auto mb-6 text-[hsl(var(--accent))]" size={40} />
              <h3 className="font-display text-3xl md:text-4xl">Enquiry received</h3>
              <p className="mt-4 text-[hsl(var(--ink-soft))] max-w-md mx-auto">
                Thank you for reaching out. Shubhra will personally respond within 48 hours.
              </p>
              <button
                onClick={() => { setSubmitted(false); setForm({ name:"",email:"",phone:"",eventType:"",date:"",guests:"",budget:"",message:"",website:"" }); setFormStartFired(false); }}
                className="mt-8 text-[11px] tracking-[0.35em] uppercase link-underline"
              >Send another enquiry</button>
            </div>
          ) : (
            <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Honeypot: hidden from users, bots often fill it */}
              <input
                type="text"
                name="website"
                value={form.website}
                onChange={(e) => setForm({ ...form, website: e.target.value })}
                tabIndex="-1"
                autoComplete="off"
                aria-hidden="true"
                style={{ position: "absolute", left: "-9999px", opacity: 0, pointerEvents: "none", height: 0, width: 0 }}
              />
              <Field label="Full Name" value={form.name} onChange={(v)=>updateField("name",v)} required />
              <Field label="Email" type="email" value={form.email} onChange={(v)=>updateField("email",v)} required />
              <Field label="Phone" value={form.phone} onChange={(v)=>updateField("phone",v)} />
              <Field label="Event Type" value={form.eventType} onChange={(v)=>updateField("eventType",v)} placeholder="Wedding, Sangeet, Reception..." />
              <Field label="Preferred Date" type="date" value={form.date} onChange={(v)=>updateField("date",v)} />
              <Field label="Guest Count" value={form.guests} onChange={(v)=>updateField("guests",v)} placeholder="e.g. 200" />
              <div className="md:col-span-2">
                <Field label="Estimated Budget" value={form.budget} onChange={(v)=>updateField("budget",v)} placeholder="USD" />
              </div>
              <div className="md:col-span-2">
                <label className="kicker">Tell us about your vision</label>
                <textarea
                  value={form.message}
                  onChange={(e)=>updateField("message",e.target.value)}
                  rows={5}
                  className="mt-3 w-full bg-transparent border-b border-[hsl(var(--line))] focus:border-[hsl(var(--ink))] outline-none py-3 text-lg font-display resize-none"
                  placeholder="Style, venue thoughts, key must-haves..."
                />
              </div>
              <div className="md:col-span-2 pt-4">
                <button
                  type="submit"
                  disabled={sending}
                  className="inline-flex items-center gap-3 border border-[hsl(var(--ink))] hover:bg-[hsl(var(--ink))] hover:text-[hsl(var(--bg))] transition-colors duration-500 px-8 py-4 text-[11px] tracking-[0.35em] uppercase disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {sending ? (
                    <>
                      <Loader2 size={14} className="animate-spin" />
                      Sending…
                    </>
                  ) : (
                    "Submit enquiry"
                  )}
                </button>
              </div>
            </form>
          )}
        </div>

        <aside className="md:col-span-5 reveal">
          <div className="img-zoom mb-10">
            <img src={philosophyImage} alt="Contact" className="w-full h-[360px] object-cover" />
          </div>
          <div className="space-y-5">
            <InfoRow icon={Mail} label="Email" value={brand.email} href={`mailto:${brand.email}`} />
            <InfoRow icon={Phone} label="Phone" value={brand.phone} href={`tel:${brand.phone}`} />
            <InfoRow icon={MapPin} label="Location" value={brand.location} />
            <div className="flex items-center gap-4 pt-4">
              <a href={brand.instagram} target="_blank" rel="noreferrer" className="hover:text-[hsl(var(--accent))]"><Instagram size={18} /></a>
              <a href={brand.facebook} target="_blank" rel="noreferrer" className="hover:text-[hsl(var(--accent))]"><Facebook size={18} /></a>
            </div>
          </div>
        </aside>
      </section>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, required }) {
  return (
    <div>
      <label className="kicker">{label}{required && <span className="text-[hsl(var(--accent))]">*</span>}</label>
      <input
        type={type}
        value={value}
        required={required}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="mt-3 w-full bg-transparent border-b border-[hsl(var(--line))] focus:border-[hsl(var(--ink))] outline-none py-3 text-lg font-display"
      />
    </div>
  );
}

function InfoRow({ icon: Icon, label, value, href }) {
  const content = (
    <div className="flex items-start gap-4">
      <Icon size={16} className="mt-1 text-[hsl(var(--accent))]" />
      <div>
        <div className="kicker">{label}</div>
        <div className="font-display text-xl mt-1">{value}</div>
      </div>
    </div>
  );
  return href ? <a href={href} className="block hover:opacity-80">{content}</a> : content;
}
